import { useMemo, useState } from "react";
import { formatDate } from "../../utils/dateUtils";
import { CAUSE_LABELS } from "../ui/SleepInterruptionModal";
import SleepEditModal from "../ui/SleepEditModal";
import LoggedByChip from "../ui/LoggedByChip";

/** Format an ISO timestamp to local HH:MM for wake-up list display. */
function wakeUpTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const SleepTable = ({ data, onUpdate, onDelete }) => {
  const [page, setPage]           = useState(1);
  const [rowsPerPage]             = useState(5);
  const [sortDir, setSortDir]     = useState("desc");
  const [editingItem, setEditing] = useState(null);
  const [isModalOpen, setModal]   = useState(false);
  const [isSaving, setSaving]     = useState(false);
  const [expanded, setExpanded]   = useState(new Set()); // row ids with visible wake-up detail

  const processedData = useMemo(() => {
    return data.map(item => {
      let duration = null;
      if (item.startTime && item.endTime) {
        const start = new Date(`2000-01-01T${item.startTime}`);
        const end   = new Date(`2000-01-01T${item.endTime}`);
        const raw   = end - start;
        const diffMs = raw > 0 ? raw : raw + 24 * 60 * 60 * 1000;
        if (diffMs > 0) {
          const hours   = Math.floor(diffMs / (1000 * 60 * 60));
          const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          duration = `${hours}h ${minutes}m`;
        }
      }
      return { ...item, duration };
    });
  }, [data]);

  const sorted = useMemo(() => {
    return processedData.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [processedData, sortDir]);

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData  = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const toggleSort = () => { setSortDir(p => p === "asc" ? "desc" : "asc"); setPage(1); };

  const totalSleep = useMemo(() => {
    return processedData.reduce((total, item) => {
      if (item.duration) {
        const match = item.duration.match(/(\d+)h (\d+)m/);
        if (match) return total + parseInt(match[1]) * 60 + parseInt(match[2]);
      }
      return total;
    }, 0);
  }, [processedData]);

  const formatTotalSleep = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins  = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleEdit = (item) => { setEditing(item); setModal(true); };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      await onUpdate(editingItem.id, formData);
      setModal(false);
      setEditing(null);
    } catch (err) {
      console.error("Error saving:", err);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        await onDelete(id);
      } catch (err) {
        console.error("Error deleting:", err);
        alert("Failed to delete entry");
      }
    }
  };

  return (
    <>
      <div className="alert alert-info mb-3">
        <strong>Total Sleep Logged:</strong> {formatTotalSleep(totalSleep)}
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col" style={{ cursor: "pointer" }} onClick={toggleSort}>
                Date {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th scope="col">Start</th>
              <th scope="col">End</th>
              <th scope="col">Duration</th>
              <th scope="col">Type</th>
              <th scope="col">Wake-ups</th>
              <th scope="col">Notes</th>
              <th scope="col">Logged by</th>
              <th scope="col" style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row) => {
              const wakeUps    = Array.isArray(row.wakeUps) ? row.wakeUps : [];
              const isExpanded = expanded.has(row.id);
              return [
                <tr key={row.id}>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.startTime}</td>
                  <td>{row.endTime || <span className="text-muted">Ongoing</span>}</td>
                  <td>{row.duration || <span className="text-muted">-</span>}</td>
                  <td>
                    <span className={`badge ${row.type === 'nap' ? 'bg-success' : 'bg-primary'}`}>
                      {row.type}
                    </span>
                  </td>
                  <td>
                    {wakeUps.length > 0 ? (
                      <button
                        className="wakeup-badge"
                        onClick={() => toggleExpand(row.id)}
                        title={isExpanded ? "Hide wake-ups" : "Show wake-ups"}
                      >
                        💤 {wakeUps.length}
                        <span style={{ fontSize: '0.6rem', marginLeft: 1 }}>
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </button>
                    ) : (
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                  <td>{row.notes || <span className="text-muted">-</span>}</td>
                  <td><LoggedByChip loggedBy={row.loggedBy} /></td>
                  <td>
                    <button
                      className="btn btn-xs btn-warning me-1"
                      title="Edit"
                      onClick={() => handleEdit(row)}
                      style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                    >✎</button>
                    <button
                      className="btn btn-xs btn-danger"
                      title="Delete"
                      onClick={() => handleDelete(row.id)}
                      style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                    >✕</button>
                  </td>
                </tr>,

                // Expandable wake-up detail row
                isExpanded && wakeUps.length > 0 && (
                  <tr key={`${row.id}-wakeups`} className="wakeup-detail-row">
                    <td colSpan={9}>
                      <ul className="wakeup-list">
                        {wakeUps.map((w, i) => (
                          <li key={i} className="wakeup-list-item">
                            <span className="wakeup-time">{wakeUpTime(w.time)}</span>
                            <span className="wakeup-cause">
                              {CAUSE_LABELS[w.cause] ?? w.cause}
                            </span>
                            <span className="wakeup-meta">
                              · {w.wentBackToSleep ? 'back to sleep ✓' : 'stayed awake'}
                              {w.feedLogged && w.cause === 'bottle' && w.bottleAmount
                                ? ` · ${w.bottleAmount} oz bottle logged`
                                : ''}
                              {w.feedLogged && w.cause === 'breastfeeding' && w.bfDuration
                                ? ` · ${w.bfDuration} min nursing logged`
                                : ''}
                              {w.notes ? ` · ${w.notes}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav aria-label="Page navigation">
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}>
                Previous
              </button>
            </li>
            {[...Array(pageCount)].map((_, idx) => {
              const num = idx + 1;
              return (
                <li key={num} className={`page-item ${num === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>{num}</button>
                </li>
              );
            })}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage(p => Math.min(p + 1, pageCount))} disabled={page === pageCount}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      <SleepEditModal
        isOpen={isModalOpen}
        item={editingItem}
        onSave={handleSave}
        onCancel={() => { setModal(false); setEditing(null); }}
        isLoading={isSaving}
      />
    </>
  );
};

export default SleepTable;
